
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

export type BlogStatsData = {
  total_posts: number;
  published_posts: number;
  draft_posts: number;
  scheduled_posts: number;
  posts_by_month: Array<{
    month: string;
    count: number;
  }>;
  categories: Array<{
    category: string;
    count: number;
  }>;
};

export function useBlogStats() {
  const { hasPermission } = usePermissions();
  
  return useQuery({
    queryKey: ["blog-stats"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('get_blog_stats');
        
        if (error) {
          console.error("Error fetching blog stats:", error);
          throw error;
        }
        
        // Validate received data
        if (!data || typeof data !== 'object') {
          throw new Error("Invalid data format received from database");
        }
        
        // Cast data to any to safely access properties
        const dataObj = data as any;
        
        // Ensure posts_by_month is an array
        const postsPerMonth = Array.isArray(dataObj.posts_by_month) 
          ? dataObj.posts_by_month 
          : [];
        
        // Ensure categories is an array
        const categories = Array.isArray(dataObj.categories)
          ? dataObj.categories
          : [];
        
        // Validate numeric fields with safe type checking
        const statsData: BlogStatsData = {
          total_posts: typeof dataObj.total_posts === 'number' ? dataObj.total_posts : 0,
          published_posts: typeof dataObj.published_posts === 'number' ? dataObj.published_posts : 0,
          draft_posts: typeof dataObj.draft_posts === 'number' ? dataObj.draft_posts : 0,
          scheduled_posts: typeof dataObj.scheduled_posts === 'number' ? dataObj.scheduled_posts : 0,
          posts_by_month: postsPerMonth,
          categories: categories
        };
        
        return statsData;
      } catch (error) {
        console.error("Error fetching blog stats:", error);
        toast.error("Failed to load blog statistics");
        
        // Return placeholder data on error
        return {
          total_posts: 0,
          published_posts: 0,
          draft_posts: 0,
          scheduled_posts: 0,
          posts_by_month: [],
          categories: []
        } as BlogStatsData;
      }
    },
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
  });
}
